import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal from '../modal/modal';
import P from '../texts/p/P';
import DatePicker from '../datepicker/DatePicker';
import { useToast } from '../toast/toast';
import theme from '../../styles/theme';
import {
  executeWithdrawal,
  getContributionWithdrawal,
  getWithdrawalPreview,
} from '../../services/GuaranteesService';
import { useConfig } from '../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';

interface WithdrawalPreview {
  capital_contribution_id: number;
  protector_id: number;
  fund_id: number | null;
  applied_quotation: number | null;
  integrated_ars: number;
  integrated_usd: number;
  afronte_ars: number;
  afronte_usd: number;
  honra_ars: number;
  honra_usd: number;
}

interface ContributionWithdrawalHeader {
  applied_quotation: number | null;
  integrated_amount_ars: number;
  integrated_amount_usd: number;
  afronte_amount_ars: number;
  afronte_amount_usd: number;
  honra_amount_ars: number;
  honra_amount_usd: number;
  net_yield_amount_ars: number;
  net_yield_amount_usd: number;
  withdrawal_amount_ars: number;
  withdrawal_amount_usd: number;
  certificate_amount_ars: number;
  certificate_amount_usd: number;
  withdrawn_at: string;
}

interface WithdrawContributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  contributionId: number;
  contributionDate?: string;
  fundName?: string;
  protectorName?: string;
  protectorId?: number | string;
  readOnly?: boolean;
  onSuccess?: () => void;
}

const InfoBanner = styled.div<{ $tone?: 'info' | 'warning' }>`
  background: ${({ $tone }) =>
    $tone === 'info'
      ? theme.colors.info.bg.inverted
      : theme.colors.warning.bg.weak};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'info'
        ? theme.colors.info.border.regular
        : theme.colors.warning.border.regular};
  border-radius: ${theme.borderRadius['border-radius-4']};
  padding: 12px 16px;
  margin-bottom: 16px;

  p {
    color: ${({ $tone }) =>
      $tone === 'info'
        ? theme.colors.info.text.regular
        : theme.colors.neutral.text.strong};
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 16px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
  margin-bottom: 20px;
`;

const MetaLabel = styled.span`
  font-weight: 700;
  color: ${theme.colors.neutral.text.strong};
`;

const MetaValue = styled.span`
  font-weight: 400;
  color: ${theme.colors.neutral.text.regular};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${theme.colors.neutral.border.weaker};
    text-align: left;
    color: ${theme.colors.neutral.text.strong};
    font-size: 14px;
  }

  th {
    background: ${theme.colors.neutral.bg.regular};
    font-size: 13px;
    font-weight: 600;
    color: ${theme.colors.neutral.text.strong};
    text-transform: none;
  }

  td:nth-child(2),
  td:nth-child(3),
  th:nth-child(2),
  th:nth-child(3) {
    text-align: right;
  }
`;

const HighlightRow = styled.tr`
  td {
    color: ${theme.colors.success.bg.regular};
    font-weight: 700;
  }
`;

const BoldRow = styled.tr`
  td {
    font-weight: 700;
    color: ${theme.colors.neutral.text.strong};
  }
`;

const NumberInput = styled.input`
  width: 140px;
  padding: 6px 8px;
  border: 1px solid ${theme.colors.neutral.border?.weak || '#DFE6EB'};
  border-radius: 6px;
  text-align: right;
  font-size: 14px;
`;

const FormulaNote = styled.div`
  margin-top: 12px;
`;

const DateField = styled.div`
  min-width: 220px;
  margin-bottom: 16px;
`;

const EXECUTE_FORMULA_LEGEND =
  'Total a retirar = Integrado − Afrontado + Honrado + Rendimiento neto. Certificado = Afrontado − Honrado. Al ejecutar se genera el movimiento negativo por (Integrado − Afrontado + Honrado), por moneda, y el aporte queda RETIRADO.';

export const toYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateOnly = (raw?: string): Date | null => {
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
};

export const isValidWithdrawalBusinessDate = (
  withdrawalYmd: string,
  contributionDateRaw?: string,
  today: Date = new Date()
): boolean => {
  const withdrawal = parseDateOnly(withdrawalYmd);
  if (!withdrawal) return false;
  const todayDate = parseDateOnly(toYmd(today));
  if (!todayDate || withdrawal > todayDate) return false;
  const contribution = parseDateOnly(contributionDateRaw);
  if (contribution && withdrawal < contribution) return false;
  return true;
};

const formatArs = (value: number) => {
  const abs = Math.abs(Math.round(value));
  const formatted = abs.toLocaleString('es-AR', { maximumFractionDigits: 0 });
  return value < 0 ? `-${formatted}` : formatted;
};

export const formatUsd = (value: number) => {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-${formatted}` : formatted;
};

const formatDisplayDate = (raw?: string | null) => {
  if (!raw) return '-';
  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

const formatDateOnlyEsAr = (raw?: string | null) => {
  const parsed = parseDateOnly(raw ?? undefined);
  if (parsed) {
    return parsed.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  return formatDisplayDate(raw);
};

const parseNumberInput = (raw: string): number => {
  if (!raw || raw.trim() === '' || raw === '-' || raw === '.') return 0;
  const normalized = raw.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapWithdrawalHeader = (data: Record<string, unknown>): ContributionWithdrawalHeader => ({
  applied_quotation:
    (data.applied_quotation as number | null) ??
    (data.appliedQuotation as number | null) ??
    null,
  integrated_amount_ars: Number(
    data.integrated_amount_ars ?? data.integratedAmountArs ?? 0
  ),
  integrated_amount_usd: Number(
    data.integrated_amount_usd ?? data.integratedAmountUsd ?? 0
  ),
  afronte_amount_ars: Number(data.afronte_amount_ars ?? data.afronteAmountArs ?? 0),
  afronte_amount_usd: Number(data.afronte_amount_usd ?? data.afronteAmountUsd ?? 0),
  honra_amount_ars: Number(data.honra_amount_ars ?? data.honraAmountArs ?? 0),
  honra_amount_usd: Number(data.honra_amount_usd ?? data.honraAmountUsd ?? 0),
  net_yield_amount_ars: Number(
    data.net_yield_amount_ars ?? data.netYieldAmountArs ?? 0
  ),
  net_yield_amount_usd: Number(
    data.net_yield_amount_usd ?? data.netYieldAmountUsd ?? 0
  ),
  withdrawal_amount_ars: Number(
    data.withdrawal_amount_ars ?? data.withdrawalAmountArs ?? 0
  ),
  withdrawal_amount_usd: Number(
    data.withdrawal_amount_usd ?? data.withdrawalAmountUsd ?? 0
  ),
  certificate_amount_ars: Number(
    data.certificate_amount_ars ?? data.certificateAmountArs ?? 0
  ),
  certificate_amount_usd: Number(
    data.certificate_amount_usd ?? data.certificateAmountUsd ?? 0
  ),
  withdrawn_at: String(data.withdrawn_at ?? data.withdrawnAt ?? ''),
});

const WithdrawContributionModal = ({
  isOpen,
  onClose,
  contributionId,
  contributionDate,
  fundName,
  protectorName,
  protectorId,
  readOnly = false,
  onSuccess,
}: WithdrawContributionModalProps) => {
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [preview, setPreview] = useState<WithdrawalPreview | null>(null);
  const [withdrawalHeader, setWithdrawalHeader] =
    useState<ContributionWithdrawalHeader | null>(null);
  const [missingWithdrawalData, setMissingWithdrawalData] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [netYieldArsInput, setNetYieldArsInput] = useState('0');
  const [netYieldUsdInput, setNetYieldUsdInput] = useState('0');
  const [withdrawalDate, setWithdrawalDate] = useState(toYmd(new Date()));
  const contributionMinDate = useMemo(
    () => parseDateOnly(contributionDate) ?? undefined,
    [contributionDate]
  );
  const todayMaxDate = useMemo(() => parseDateOnly(toYmd(new Date())) ?? new Date(), []);
  const isWithdrawalDateValid = useMemo(
    () => isValidWithdrawalBusinessDate(withdrawalDate, contributionDate),
    [withdrawalDate, contributionDate]
  );

  useEffect(() => {
    if (!isOpen) return;

    if (readOnly) {
      const loadWithdrawal = async () => {
        setIsLoadingPreview(true);
        setPreview(null);
        setWithdrawalHeader(null);
        setMissingWithdrawalData(false);
        try {
          const response = await getContributionWithdrawal(
            contributionId,
            protectorId ?? ''
          );
          if (response.success && 'data' in response && response.data) {
            setWithdrawalHeader(
              mapWithdrawalHeader(response.data as Record<string, unknown>)
            );
          } else if (
            ('status' in response && response.status === 404) ||
            !response.success
          ) {
            setMissingWithdrawalData(true);
          } else {
            setMissingWithdrawalData(true);
          }
        } catch (error) {
          console.error('Error loading contribution withdrawal:', error);
          setMissingWithdrawalData(true);
        } finally {
          setIsLoadingPreview(false);
        }
      };

      loadWithdrawal();
      return;
    }

    const loadPreview = async () => {
      setIsLoadingPreview(true);
      setWithdrawalHeader(null);
      setMissingWithdrawalData(false);
      setNetYieldArsInput('0');
      setNetYieldUsdInput('0');
      setWithdrawalDate(toYmd(new Date()));
      try {
        const response = await getWithdrawalPreview(contributionId);
        if (response.success && 'data' in response && response.data) {
          setPreview(response.data);
        } else {
          const errorMsg =
            'message' in response
              ? response.message
              : 'Error al calcular el retiro del aporte';
          useToast.error({
            title: 'Error',
            description: errorMsg,
            id: 'default-toast',
            autoClose: 5000,
            closeButton: true,
          });
          onClose();
        }
      } catch (error) {
        console.error('Error loading withdrawal preview:', error);
        useToast.error({
          title: 'Error',
          description: 'Error al calcular el retiro del aporte',
          id: 'default-toast',
          autoClose: 5000,
          closeButton: true,
        });
        onClose();
      } finally {
        setIsLoadingPreview(false);
      }
    };

    loadPreview();
  }, [isOpen, contributionId, readOnly, protectorId]);

  const netYieldArs = useMemo(
    () => parseNumberInput(netYieldArsInput),
    [netYieldArsInput]
  );
  const netYieldUsd = useMemo(
    () => parseNumberInput(netYieldUsdInput),
    [netYieldUsdInput]
  );

  const totals = useMemo(() => {
    if (!preview) {
      return {
        withdrawArs: 0,
        withdrawUsd: 0,
        certificateArs: 0,
        certificateUsd: 0,
      };
    }
    return {
      withdrawArs:
        preview.integrated_ars -
        preview.afronte_ars +
        preview.honra_ars +
        netYieldArs,
      withdrawUsd:
        preview.integrated_usd -
        preview.afronte_usd +
        preview.honra_usd +
        netYieldUsd,
      certificateArs: preview.afronte_ars - preview.honra_ars,
      certificateUsd: preview.afronte_usd - preview.honra_usd,
    };
  }, [preview, netYieldArs, netYieldUsd]);

  const handleClose = () => {
    if (isSubmitting) return;
    setPreview(null);
    setWithdrawalHeader(null);
    setMissingWithdrawalData(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (readOnly || !isWithdrawalDateValid) return;
    setIsSubmitting(true);
    try {
      const response = await executeWithdrawal(contributionId, {
        net_yield_ars: netYieldArs,
        net_yield_usd: netYieldUsd,
        withdrawal_date: withdrawalDate,
      });
      if (response.success) {
        useToast.success({
          title: 'Retiro ejecutado',
          description: 'El aporte fue retirado correctamente',
          id: 'default-toast',
          autoClose: 5000,
          closeButton: true,
        });
        onSuccess?.();
        handleClose();
      } else {
        const errorMsg =
          'message' in response ? response.message : 'Error al ejecutar el retiro';
        useToast.error({
          title: 'Error',
          description: errorMsg,
          id: 'default-toast',
          autoClose: 5000,
          closeButton: true,
        });
      }
    } catch (error) {
      console.error('Error executing withdrawal:', error);
      useToast.error({
        title: 'Error',
        description: 'Error al ejecutar el retiro',
        id: 'default-toast',
        autoClose: 5000,
        closeButton: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const appliedQuotation = readOnly
    ? withdrawalHeader?.applied_quotation
    : preview?.applied_quotation;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Retiro del aporte"
      width="760px"
      primaryText={readOnly ? 'Cerrar' : 'Ejecutar retiro'}
      primaryVariant={readOnly ? 'secondary' : 'secondary'}
      onPrimaryAction={readOnly ? handleClose : handleSubmit}
      primaryActionDisabled={
        readOnly
          ? false
          : isLoadingPreview || isSubmitting || !preview || !isWithdrawalDateValid
      }
      loadingPrimaryBtn={!readOnly && isSubmitting}
      secondaryText={readOnly ? '' : 'Cancelar'}
      secondaryVariant="contained"
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      {readOnly ? (
        <>
          <InfoBanner $tone="info">
            <P type="4" withMargin={false} variant="info">
              Retiro ya ejecutado. Los importes son los calculados al momento del retiro.
            </P>
          </InfoBanner>

          <MetaGrid>
            <P type="4" withMargin={false} variant="neutral">
              <MetaLabel>Aporte: </MetaLabel>
              <MetaValue>{formatDateOnlyEsAr(contributionDate)}</MetaValue>
            </P>
            <P type="4" withMargin={false} variant="neutral">
              <MetaLabel>Fondo: </MetaLabel>
              <MetaValue>{fundName || '-'}</MetaValue>
            </P>
            <P type="4" withMargin={false} variant="neutral">
              <MetaLabel>Fecha de retiro: </MetaLabel>
              <MetaValue>
                {formatDateOnlyEsAr(withdrawalHeader?.withdrawn_at)}
              </MetaValue>
            </P>
            <P type="4" withMargin={false} variant="neutral">
              <MetaLabel>Cotización USD: </MetaLabel>
              <MetaValue>
                {appliedQuotation != null
                  ? `$ ${formatArs(Number(appliedQuotation))}`
                  : '-'}
              </MetaValue>
            </P>
          </MetaGrid>

          {isLoadingPreview ? (
            <P type="3" withMargin={false}>
              Cargando datos del retiro...
            </P>
          ) : missingWithdrawalData ? (
            <P type="3" withMargin={false}>
              Sin datos del retiro
            </P>
          ) : withdrawalHeader ? (
            <Table>
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th>Pesos ({localCurrencyCode})</th>
                  <th>Dólares (USD)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total integrado</td>
                  <td>{formatArs(withdrawalHeader.integrated_amount_ars)}</td>
                  <td>{formatUsd(withdrawalHeader.integrated_amount_usd)}</td>
                </tr>
                <tr>
                  <td>Total afrontado</td>
                  <td>{formatArs(withdrawalHeader.afronte_amount_ars)}</td>
                  <td>{formatUsd(withdrawalHeader.afronte_amount_usd)}</td>
                </tr>
                <tr>
                  <td>Total honrado (recuperos)</td>
                  <td>{formatArs(withdrawalHeader.honra_amount_ars)}</td>
                  <td>{formatUsd(withdrawalHeader.honra_amount_usd)}</td>
                </tr>
                <tr>
                  <td>Total rendimiento neto (manual)</td>
                  <td>{formatArs(withdrawalHeader.net_yield_amount_ars)}</td>
                  <td>{formatUsd(withdrawalHeader.net_yield_amount_usd)}</td>
                </tr>
                <BoldRow>
                  <td>Total a retirar</td>
                  <td>{formatArs(withdrawalHeader.withdrawal_amount_ars)}</td>
                  <td>{formatUsd(withdrawalHeader.withdrawal_amount_usd)}</td>
                </BoldRow>
                <HighlightRow>
                  <td>Total a retirar en certificado</td>
                  <td>{formatArs(withdrawalHeader.certificate_amount_ars)}</td>
                  <td>{formatUsd(withdrawalHeader.certificate_amount_usd)}</td>
                </HighlightRow>
              </tbody>
            </Table>
          ) : null}
        </>
      ) : (
        <>
          <InfoBanner $tone="warning">
            <P type="4" withMargin={false} variant="neutral">
              La liquidación usa las mismas reglas de siempre, calculada para este aporte
              (integraciones y afrontes/honras del aporte). Los campos &quot;Total
              rendimiento neto&quot; son editables.
            </P>
          </InfoBanner>

          <MetaRow>
            {protectorName && (
              <P type="4" weight="bold" withMargin={false} variant="neutral">
                {protectorName}
              </P>
            )}
            {contributionDate && (
              <P type="4" withMargin={false} variant="neutral">
                <MetaLabel>Aporte: </MetaLabel>
                <MetaValue>{contributionDate}</MetaValue>
              </P>
            )}
            {fundName && (
              <P type="4" withMargin={false} variant="neutral">
                <MetaLabel>Fondo: </MetaLabel>
                <MetaValue>{fundName}</MetaValue>
              </P>
            )}
            {preview?.applied_quotation != null && (
              <P type="4" withMargin={false} variant="neutral">
                <MetaLabel>Cotización USD: </MetaLabel>
                <MetaValue>
                  $ {formatArs(Number(preview.applied_quotation))}
                </MetaValue>
              </P>
            )}
          </MetaRow>

          <DateField>
            <DatePicker
              id="fecha-retiro-aporte"
              label="Fecha de retiro"
              placeholder="Fecha de retiro"
              defaultValue={withdrawalDate}
              disabled={isSubmitting}
              displayFormat="yyyy-MM-dd"
              minDate={contributionMinDate}
              maxDate={todayMaxDate}
              onChange={(value: string | null) => setWithdrawalDate(value || '')}
            />
          </DateField>

          {isLoadingPreview || !preview ? (
            <P type="3" withMargin={false}>
              Calculando conceptos del retiro...
            </P>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Pesos ({localCurrencyCode})</th>
                    <th>Dólares (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total integrado</td>
                    <td>{formatArs(preview.integrated_ars)}</td>
                    <td>{formatUsd(preview.integrated_usd)}</td>
                  </tr>
                  <tr>
                    <td>Total afrontado</td>
                    <td>{formatArs(preview.afronte_ars)}</td>
                    <td>{formatUsd(preview.afronte_usd)}</td>
                  </tr>
                  <tr>
                    <td>Total honrado (recuperos)</td>
                    <td>{formatArs(preview.honra_ars)}</td>
                    <td>{formatUsd(preview.honra_usd)}</td>
                  </tr>
                  <tr>
                    <td>Total rendimiento neto (manual)</td>
                    <td>
                      <NumberInput
                        type="text"
                        value={netYieldArsInput}
                        onChange={(e) => setNetYieldArsInput(e.target.value)}
                        aria-label={`Rendimiento neto ${localCurrencyCode}`}
                      />
                    </td>
                    <td>
                      <NumberInput
                        type="text"
                        value={netYieldUsdInput}
                        onChange={(e) => setNetYieldUsdInput(e.target.value)}
                        aria-label="Rendimiento neto USD"
                      />
                    </td>
                  </tr>
                  <BoldRow>
                    <td>Total a retirar</td>
                    <td>{formatArs(totals.withdrawArs)}</td>
                    <td>{formatUsd(totals.withdrawUsd)}</td>
                  </BoldRow>
                  <HighlightRow>
                    <td>Total a retirar en certificado</td>
                    <td>{formatArs(totals.certificateArs)}</td>
                    <td>{formatUsd(totals.certificateUsd)}</td>
                  </HighlightRow>
                </tbody>
              </Table>

              <FormulaNote>
                <P type="4" withMargin={false} variant="neutral" modifier="weak">
                  {EXECUTE_FORMULA_LEGEND}
                </P>
              </FormulaNote>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default WithdrawContributionModal;
